<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import educationImage from '@/images/logo.jpg'
import { createResource } from '@frappe-ui/resources/resources.js'
import { getErrorMessage } from '@/utils/error'
import { toast } from '@/services/toast'

const router = useRouter()
const { t } = useI18n()
const fullName = ref('')
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const errorMessage = ref('')

const registration = createResource({
  url: 'retiora.api.auth.register',
  auto: false,
  onSuccess() {
    toast(t('login.registrationSuccess'), 'success')
    router.replace({ name: 'login', query: { registered: '1' } })
  },
})

async function handleRegister() {
  errorMessage.value = ''

  if (password.value !== confirmPassword.value) {
    errorMessage.value = t('login.passwordMismatch')
    return
  }

  try {
    await registration.submit({
      full_name: fullName.value.trim(),
      email: email.value.trim(),
      password: password.value,
    })
  } catch (error) {
    errorMessage.value = getErrorMessage(error, t('login.registrationError'))
    toast(errorMessage.value, 'error')
  }
}
</script>

<template>
  <main class="register-page">
    <section class="branding-panel">
      <div class="branding-content">
        <img
          :src="educationImage"
          :alt="t('login.illustrationAlt')"
          class="education-image"
        />
        <div class="branding-copy">
          <h1>{{ t('login.empoweringEducation') }}</h1>
          <p>{{ t('login.brandingMessage') }}</p>
        </div>
      </div>
    </section>

    <section
      class="register-panel"
      aria-labelledby="register-title"
    >
      <div class="register-content">
        <div class="brand-name">Retiora</div>
        <div class="register-heading">
          <h2 id="register-title">{{ t('login.registerTitle') }}</h2>
          <p>{{ t('login.registerMessage') }}</p>
        </div>

        <form
          class="register-form"
          @submit.prevent="handleRegister"
        >
          <div class="field">
            <label for="full-name">{{ t('login.fullName') }}</label>
            <InputText
              id="full-name"
              v-model="fullName"
              fluid
              autocomplete="name"
              required
            />
          </div>

          <div class="field">
            <label for="register-email">{{ t('login.emailOrUsername') }}</label>
            <InputText
              id="register-email"
              v-model="email"
              type="email"
              fluid
              autocomplete="email"
              required
            />
          </div>

          <div class="field">
            <label for="register-password">{{ t('login.password') }}</label>
            <Password
              v-model="password"
              input-id="register-password"
              :feedback="false"
              toggle-mask
              fluid
              required
              :input-props="{ autocomplete: 'new-password', minlength: 8 }"
            />
          </div>

          <div class="field">
            <label for="confirm-password">{{ t('login.confirmPassword') }}</label>
            <Password
              v-model="confirmPassword"
              input-id="confirm-password"
              :feedback="false"
              toggle-mask
              fluid
              required
              :input-props="{ autocomplete: 'new-password', minlength: 8 }"
            />
          </div>

          <Button
            type="submit"
            :label="t('login.createAccount')"
            fluid
            :loading="registration.loading"
            :disabled="registration.loading"
          />

          <p
            v-if="errorMessage"
            class="register-error"
            role="alert"
          >
            {{ errorMessage }}
          </p>
        </form>

        <p class="request-access">
          {{ t('login.alreadyHaveAccount') }}
          <RouterLink :to="{ name: 'login' }">{{ t('login.signInHere') }}</RouterLink>
        </p>
      </div>
    </section>
  </main>
</template>

<style scoped>
.register-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  background: #f7f8ff;
}

.branding-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
  background:
    radial-gradient(circle at 40% 55%, rgba(204, 243, 239, 0.9), transparent 34%),
    linear-gradient(180deg, #e8f0ff 0%, #dfeaff 100%);
}

.branding-content {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.education-image {
  width: 100%;
  max-width: 550px;
  aspect-ratio: 1.5;
  object-fit: cover;
  background: #f8f7f1;
  box-shadow: 0 28px 60px rgba(21, 40, 80, 0.14);
}

.branding-copy {
  margin-top: 42px;
  text-align: center;
}

.branding-copy h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.15;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #0f172a;
}

.branding-copy p {
  max-width: 520px;
  margin: 16px auto 0;
  font-size: 20px;
  line-height: 1.55;
  color: #334155;
}

.register-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
}

.register-content {
  width: 100%;
  max-width: 490px;
}

.brand-name {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  margin-bottom: 28px;
  padding: 0 6px;
  font-size: 14px;
  font-weight: 500;
  color: #4646d8;
  background: rgba(241, 245, 249, 0.75);
}

.register-heading {
  margin-bottom: 36px;
}

.register-heading h2 {
  margin: 0;
  font-size: 48px;
  line-height: 1.08;
  letter-spacing: -0.04em;
  color: #171827;
}

.register-heading p {
  margin: 14px 0 0;
  color: #77798b;
  font-size: 17px;
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  color: #343544;
  font-size: 14px;
  font-weight: 600;
}

.register-error {
  margin: 0;
  color: #bd3030;
  font-size: 14px;
}

.request-access {
  margin-top: 28px;
  color: #77798b;
  font-size: 14px;
  text-align: center;
}

.request-access a {
  color: #4646d8;
  font-weight: 600;
  text-decoration: none;
}

.request-access a:hover {
  text-decoration: underline;
}

@media (max-width: 1023px) {
  .register-page {
    display: block;
  }

  .branding-panel {
    display: none;
  }

  .register-panel {
    min-height: 100vh;
    box-sizing: border-box;
    padding: 36px 24px;
  }

  .register-heading h2 {
    font-size: 38px;
  }
}
</style>
